import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { getDeepActiveElement } from './activeElement';
import { pushEscapeLayer } from './escapeLayers';
import { FOCUS_RING_INLINE_CONFIRM_INSET } from './focusRing';
import {
    type ChipPlacement,
    computeChipPlacement,
    isUnplaceable,
    placementKey,
} from './inlineConfirmPosition';

export interface InlineConfirmProps {
    /** Mounts the chip and moves focus into it. Nothing renders while false. */
    open: boolean;
    /** What is being confirmed, e.g. "Confirm Remove All". Becomes the chip's name. */
    message: string;
    /** Defaults to "Yes". */
    confirmLabel?: string;
    onConfirm: () => void;
    /** Called on Escape, on a click outside, and when the anchor scrolls out of view. */
    onDismiss: () => void;
    /** The control being answered for. The chip pins to its left and follows it. */
    anchorTo?: React.RefObject<HTMLElement | null>;
    /** Replaces the leading spinner. `null` drops it. */
    icon?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

const GROUND =
    'bg-[var(--malachite-inline-confirm-background)] text-[var(--malachite-inline-confirm-foreground)]';

const CONFIRM_QUIET =
    'text-[color-mix(in_srgb,var(--malachite-inline-confirm-foreground)_80%,var(--malachite-inline-confirm-background))] hover:text-[var(--malachite-inline-confirm-foreground)]';

const DIVIDER = 'w-px shrink-0 self-stretch bg-[var(--malachite-inline-confirm-divider)]';

let dismissOpenInstance: (() => void) | null = null;

const ConfirmSpinner: React.FC = () => (
    <span
        aria-hidden="true"
        className="size-[13px] shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70 motion-reduce:animate-none"
    />
);

/**
 * A confirmation asked on the control that triggered it, rather than in a modal.
 *
 * With `anchorTo` the chip places and tracks itself. Without it the chip renders in flow, and
 * must not sit in a container that clips its overflow — `Table`'s header cells among them.
 */
export const InlineConfirm: React.FC<InlineConfirmProps> = ({
    open,
    message,
    confirmLabel = 'Yes',
    onConfirm,
    onDismiss,
    anchorTo,
    icon = <ConfirmSpinner />,
    className = '',
    style,
}) => {
    const pillRef = useRef<HTMLDivElement | null>(null);
    const confirmRef = useRef<HTMLButtonElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const messageId = useId();
    const [placement, setPlacement] = useState<ChipPlacement | null>(null);
    const lastKey = useRef('');

    const onDismissRef = useRef(onDismiss);
    useLayoutEffect(() => {
        onDismissRef.current = onDismiss;
    });

    useLayoutEffect(() => {
        if (!open) {
            return;
        }

        const dismiss = () => onDismissRef.current();

        const handleMouseDown = (event: MouseEvent) => {
            if (event.button !== 0) {
                return;
            }

            const path = event.composedPath();
            const pill = pillRef.current;
            const anchor = anchorTo?.current;
            if ((pill && path.includes(pill)) || (anchor && path.includes(anchor))) {
                return;
            }

            dismiss();
        };

        dismissOpenInstance?.();
        dismissOpenInstance = dismiss;
        const releaseEscape = pushEscapeLayer(dismiss);
        document.addEventListener('mousedown', handleMouseDown, true);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown, true);
            releaseEscape();
            if (dismissOpenInstance === dismiss) {
                dismissOpenInstance = null;
            }
        };
    }, [open, anchorTo]);

    // `scroll` does not cross a shadow boundary, so the rect is polled, as `Dropdown` does.
    // Layout, not passive: the first pass must measure before the chip is painted.
    useLayoutEffect(() => {
        if (!open || !anchorTo) {
            setPlacement(null);
            lastKey.current = '';
            return;
        }

        let frame = 0;
        const follow = () => {
            const anchor = anchorTo.current;
            const rect = anchor?.getBoundingClientRect();

            // An anchor that has unmounted is as unpointable as one scrolled out of view,
            // and leaving the chip up would leave it focused and detached from any control.
            if (!rect || isUnplaceable(rect, window.innerWidth, window.innerHeight)) {
                onDismissRef.current();
                return;
            }

            const chipWidth = pillRef.current?.offsetWidth ?? 0;
            const chipHeight = pillRef.current?.offsetHeight ?? 0;
            const { clientWidth: viewportWidth, clientHeight: viewportHeight } =
                document.documentElement;
            const key = placementKey(rect, chipWidth, chipHeight, viewportWidth, viewportHeight);
            if (key !== lastKey.current) {
                lastKey.current = key;
                setPlacement(
                    computeChipPlacement({
                        anchorRect: rect,
                        chipWidth,
                        chipHeight,
                        viewportWidth,
                        viewportHeight,
                    }),
                );
            }

            frame = requestAnimationFrame(follow);
        };
        follow();

        return () => cancelAnimationFrame(frame);
    }, [open, anchorTo]);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }

        previousFocusRef.current = getDeepActiveElement();
        confirmRef.current?.focus();

        return () => {
            const previous = previousFocusRef.current;
            const pill = pillRef.current;
            const active = getDeepActiveElement();
            previousFocusRef.current = null;

            if (!previous?.isConnected || (active && pill && !pill.contains(active))) {
                return;
            }

            // Without `preventScroll` a scroll-away dismissal would focus the anchor the user
            // just scrolled past, and the browser would jump the page back to it.
            previous.focus({ preventScroll: true });
        };
    }, [open]);

    if (!open) {
        return null;
    }

    const placementStyle: React.CSSProperties | undefined = anchorTo
        ? {
              position: 'fixed',
              top: placement?.top ?? 0,
              left: placement?.left ?? 0,
              maxWidth: placement?.maxWidth,
              opacity: placement ? 1 : 0,
          }
        : undefined;

    return (
        <div
            ref={pillRef}
            role="alertdialog"
            aria-labelledby={messageId}
            className={`flex h-[33px] w-fit items-center gap-3 overflow-clip rounded-xl pl-[10px] ${GROUND} ${className}`.trim()}
            style={{
                boxShadow: 'var(--malachite-inline-confirm-shadow)',
                ...placementStyle,
                ...style,
            }}
        >
            {icon}
            <p
                id={messageId}
                className="m-0 min-w-0 truncate text-[13px] font-medium leading-[1.6] tracking-[0.13px]"
            >
                {message}
            </p>
            <span aria-hidden="true" className={DIVIDER} />
            <button
                ref={confirmRef}
                type="button"
                onClick={onConfirm}
                onKeyDown={(event) => {
                    // A held Enter on the control that opened this must not confirm it.
                    if (event.repeat && event.key === 'Enter') {
                        event.preventDefault();
                    }
                }}
                className={`${CONFIRM_QUIET} flex h-full min-w-[39px] shrink-0 cursor-pointer items-center justify-center whitespace-nowrap border-0 bg-transparent px-2 text-[13px] font-normal leading-[1.6] tracking-[0.13px] transition-colors ${FOCUS_RING_INLINE_CONFIRM_INSET}`}
            >
                {confirmLabel}
            </button>
            <span aria-hidden="true" className={DIVIDER} />
        </div>
    );
};

export default InlineConfirm;
